/**
 * Company Branding — the OWNING company's brand mark (logo + display name),
 * keyed by `companyId` (William's definitive branding model, 2026-06-10).
 *
 * Site cards, report cards, and the audience-default labels must wear the
 * brand of the site's OWNING company (`site.companyId`) — never the viewer's
 * session/theme/template brand. This module resolves that brand from:
 *   - companies/{cid}/settings/branding.logoUrl   (same doc the store's
 *     `loadBrandLogos` → `fetchOrgLogoUrl` reads for the VIEWER's org)
 *   - companies/{cid}.name                        (company display name)
 *
 * Access (firestore.rules): both docs are readable by `isInCompanyTree(cid)`
 * (own org + descendants) or platform admin. A NON-admin viewing a SIBLING
 * org's site (cross-tenant) is rules-denied — each read fails closed to
 * `null` so callers gracefully fall back to the v1.3.2 ownership-guard
 * behaviour (`resolveSiteCardBranding`'s suppression branch). Members always
 * resolve their OWN org's brand; admins/parents resolve the whole subtree.
 *
 * Caching: one in-flight/settled Promise per companyId for the session
 * (module-level Map). Denied/unset lookups cache `{null,null}` too, so a
 * dashboard with many cards never hammers Firestore. Imports are STATIC
 * (mirroring `brandLogoService`); every Firestore access happens inside a
 * per-read try/catch, so a minimal `firebase/firestore` test stub without
 * `getDoc` degrades to `{null,null}` instead of crashing the surface.
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { isLocalMockMode } from './localDev/isLocalMockMode';

export interface CompanyBranding {
  /** The org's brand logo (proxy-relative storage URL), or null when unset/denied. */
  logoUrl: string | null;
  /** The org's display name (`companies/{cid}.name`), or null when unset/denied. */
  companyName: string | null;
}

const EMPTY_BRANDING: CompanyBranding = { logoUrl: null, companyName: null };

/** Session-lifetime cache — companyId → in-flight/settled branding promise. */
const cache = new Map<string, Promise<CompanyBranding>>();

async function loadCompanyBranding(companyId: string): Promise<CompanyBranding> {
  // Two independent reads; each fails closed to null on its own (the company
  // NAME can still resolve when the branding doc is missing, and vice versa).
  const [logoUrl, companyName] = await Promise.all([
    (async (): Promise<string | null> => {
      try {
        const snap = await getDoc(doc(db, `companies/${companyId}/settings`, 'branding'));
        if (snap.exists()) {
          const url = (snap.data() as { logoUrl?: unknown }).logoUrl;
          if (typeof url === 'string' && url.trim().length > 0) return url;
        }
      } catch {
        /* rules-denied (cross-tenant non-admin) or transient — fall back */
      }
      return null;
    })(),
    (async (): Promise<string | null> => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (snap.exists()) {
          const name = (snap.data() as { name?: unknown }).name;
          if (typeof name === 'string' && name.trim().length > 0) return name;
        }
      } catch {
        /* rules-denied (cross-tenant non-admin) or transient — fall back */
      }
      return null;
    })(),
  ]);
  return { logoUrl, companyName };
}

/**
 * Resolve the owning company's brand `{logoUrl, companyName}` for a
 * `companyId`, memoised for the session. Never rejects — every failure mode
 * (no id, missing docs, rules-denied reads) resolves `{null, null}` so the
 * caller's brand chain falls back to current behaviour.
 */
export function companyBrandingFor(companyId: string | null | undefined): Promise<CompanyBranding> {
  const id = typeof companyId === 'string' ? companyId.trim() : '';
  if (id.length === 0) return Promise.resolve(EMPTY_BRANDING);
  // Offline fixture sites intentionally use a synthetic company id. Resolving
  // it through Firestore violates local-mock's no-provider contract and causes
  // the WebChannel transport to emit a Google clear-dot probe when offline.
  // The DEV-gated fixture has no owning-company logo, so fail closed to the
  // normal brand fallback without initiating any cloud read.
  if (isLocalMockMode()) return Promise.resolve(EMPTY_BRANDING);
  const hit = cache.get(id);
  if (hit) return hit;
  const pending = loadCompanyBranding(id);
  cache.set(id, pending);
  return pending;
}

/** Test seam — drop all memoised lookups (cache is session-lifetime in prod). */
export function clearCompanyBrandingCache(): void {
  cache.clear();
}

/**
 * Owning-company branding for ONE site (`site.companyId`). Returns `null`
 * while loading or when the site is personal (no companyId) — callers treat
 * `null` exactly like "no owning brand available" and keep their fallback.
 */
export function useCompanyBranding(companyId: string | null | undefined): CompanyBranding | null {
  const id = typeof companyId === 'string' && companyId.trim().length > 0 ? companyId.trim() : null;
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  useEffect(() => {
    if (!id) {
      setBranding(null);
      return;
    }
    let cancelled = false;
    void companyBrandingFor(id).then((resolved) => {
      if (!cancelled) setBranding(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);
  return id ? branding : null;
}

/**
 * Owning-company branding for a LIST of sites (the dashboard site-card map).
 * Pass the visible sites' `companyId`s (nullish entries are fine — they're
 * personal sites); resolves the DISTINCT ids once each through the shared
 * cache and returns a `companyId → CompanyBranding` record. Entries appear
 * when their lookups settle; missing key = still loading / personal site.
 */
export function useCompaniesBranding(
  companyIds: ReadonlyArray<string | null | undefined>,
): Record<string, CompanyBranding> {
  // Order-stable distinct-id key so the effect re-runs only when the SET of
  // owning companies changes, not on every new array identity from a render.
  // '/' is a safe separator — Firestore document ids can never contain it.
  const key = useMemo(() => {
    const distinct = Array.from(
      new Set(
        companyIds
          .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
          .map((id) => id.trim()),
      ),
    ).sort();
    return distinct.join('/');
  }, [companyIds]);
  const [map, setMap] = useState<Record<string, CompanyBranding>>({});
  useEffect(() => {
    const ids = key.length > 0 ? key.split('/') : [];
    if (ids.length === 0) {
      setMap({});
      return;
    }
    let cancelled = false;
    void Promise.all(
      ids.map(async (id) => [id, await companyBrandingFor(id)] as const),
    ).then((entries) => {
      if (!cancelled) setMap(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return map;
}
